import {
  VAULT_PROVIDERS,
  type ProfileVaultSummary,
  type VaultConflictResolutions,
  type VaultRecoveryPreview,
  type VaultSnapshot,
} from "@shared/profileVault";
import { setVaultKey } from "@/hooks/useKeyVault";

export type VaultApplyResponse =
  | { status: "ok"; summary: ProfileVaultSummary; vault: VaultSnapshot }
  | { status: "stale"; preview: VaultRecoveryPreview };

function recoveryError(): Error {
  return new Error("Cloud recovery could not be completed. Review the recovery copy and try again.");
}

async function parseJson(response: Response): Promise<Record<string, unknown>> {
  try {
    const data = await response.json();
    return data && typeof data === "object" ? data as Record<string, unknown> : {};
  } catch {
    return {};
  }
}

export async function requestVaultPreview(localVault: VaultSnapshot): Promise<VaultRecoveryPreview> {
  const response = await fetch("/api/profile/vault/preview", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ localVault }),
  });
  const data = await parseJson(response);
  if (!response.ok || data.status !== "ok" || !data.preview || typeof data.preview !== "object") {
    throw recoveryError();
  }
  return data.preview as VaultRecoveryPreview;
}

export async function applyVaultMerge(
  localVault: VaultSnapshot,
  revision: number,
  resolutions: VaultConflictResolutions,
): Promise<VaultApplyResponse> {
  const response = await fetch("/api/profile/vault/apply", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ localVault, revision, resolutions }),
  });
  const data = await parseJson(response);
  if (response.status === 409 && data.status === "stale" && data.preview && typeof data.preview === "object") {
    return { status: "stale", preview: data.preview as VaultRecoveryPreview };
  }
  if (!response.ok || data.status !== "ok" || !data.summary || !data.vault
    || typeof data.summary !== "object" || typeof data.vault !== "object") {
    throw recoveryError();
  }
  return {
    status: "ok",
    summary: data.summary as ProfileVaultSummary,
    vault: data.vault as VaultSnapshot,
  };
}

export function applyRecoveredVaultFromResponse(response: VaultApplyResponse): {
  applied: boolean;
  preview?: VaultRecoveryPreview;
} {
  if (response.status !== "ok") {
    return { applied: false, preview: response.preview };
  }
  for (const provider of VAULT_PROVIDERS) {
    const entry = response.vault[provider];
    if (entry) setVaultKey(provider, entry.value);
  }
  return { applied: true };
}

export async function deleteCloudVault(confirmation: string): Promise<ProfileVaultSummary> {
  const response = await fetch("/api/profile/vault", {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ confirmation }),
  });
  const data = await parseJson(response);
  if (!response.ok || data.status !== "ok" || !data.summary || typeof data.summary !== "object") {
    throw recoveryError();
  }
  return data.summary as ProfileVaultSummary;
}
